/**
 * VideoPlayer.js
 *
 * Full-screen cinematic video player for the Forge Retreat intro film.
 *
 * Behaviour:
 *  - Opens full-screen when triggered (play button or ?forgeintro in URL)
 *  - On mobile/tablet: requests landscape orientation lock via ScreenOrientation API
 *  - Controls (back button, progress, mute) fade in on hover or click/tap
 *  - Back button returns the user to the page; ESC key also closes
 *  - Smooth enter/exit animation
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp, FaExpand, FaCompress } from "react-icons/fa";
import { HiArrowLeft } from "react-icons/hi2";
import "./VideoPlayer.css";

const VIDEO_SRC = "/ForgeIntro.mp4";

/* ── Format seconds to m:ss ── */
const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

/* ── Lock to landscape on mobile / release on close ── */
const lockLandscape = async () => {
  try {
    // eslint-disable-next-line no-restricted-globals
    if (window.screen?.orientation?.lock) {
      // eslint-disable-next-line no-restricted-globals
      await window.screen.orientation.lock("landscape");
    }
  } catch (_) {
    // Not supported / denied — silently ignore, video still plays full-screen
  }
};

const unlockOrientation = () => {
  try {
    // eslint-disable-next-line no-restricted-globals
    if (window.screen?.orientation?.unlock) {
      // eslint-disable-next-line no-restricted-globals
      window.screen.orientation.unlock();
    }
  } catch (_) {}
};

export default function VideoPlayer({ onClose, fromUrl = false }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimer = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(fromUrl); // URL-open needs a click before fullscreen
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0); // 0–1
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [showCtrls, setShowCtrls] = useState(true);
  const [buffered, setBuffered] = useState(0); // 0–1
  const [fullscreen, setFullscreen] = useState(false);

  /* ── On mount: go native fullscreen + lock orientation on mobile ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isMobile = window.innerWidth < 1024;

    if (!fromUrl) {
      // Button-triggered: we have a user gesture — request fullscreen immediately
      const requestFs = el.requestFullscreen?.() || el.webkitRequestFullscreen?.() || el.mozRequestFullScreen?.();
      if (requestFs) {
        requestFs
          .then(() => {
            setFullscreen(true);
            if (isMobile) lockLandscape();
          })
          .catch(() => {});
      }

      // Auto-play
      const vid = videoRef.current;
      if (vid) {
        vid
          .play()
          .then(() => setPlaying(true))
          .catch(() => {});
      }
    }
    // fromUrl: don't attempt fullscreen yet — wait for user gesture overlay click

    // ESC key fallback (browser fullscreen handles its own ESC, but
    // this catches our CSS-fullscreen fallback case)
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);

    // Fullscreen change event — sync state
    const onFsChange = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setFullscreen(isFs);
      if (!isFs) handleClose();
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      clearTimeout(controlsTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Enter fullscreen on gesture (URL-opened player) ── */
  const enterFullscreenOnGesture = async () => {
    setNeedsGesture(false);
    const el = containerRef.current;
    const vid = videoRef.current;
    const isMobile = window.innerWidth < 1024;

    if (el) {
      try {
        await (el.requestFullscreen?.() || el.webkitRequestFullscreen?.() || Promise.resolve());
        setFullscreen(true);
        if (isMobile) lockLandscape();
      } catch (_) {}
    }
    if (vid) {
      vid
        .play()
        .then(() => setPlaying(true))
        .catch(() => {});
    }
  };

  /* ── Close / exit ── */
  const handleClose = useCallback(() => {
    const vid = videoRef.current;
    if (vid) {
      vid.pause();
    }
    unlockOrientation();
    // Exit native fullscreen if active
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen?.() || document.webkitExitFullscreen?.())?.catch(() => {});
    }
    onClose();
  }, [onClose]);

  /* ── Controls auto-hide ── */
  const showControls = useCallback(() => {
    setShowCtrls(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowCtrls(false);
    }, 3000);
  }, []);

  /* ── Video events ── */
  const onTimeUpdate = () => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    setCurrent(vid.currentTime);
    setProgress(vid.currentTime / vid.duration);
    // Buffered
    if (vid.buffered.length > 0) {
      setBuffered(vid.buffered.end(vid.buffered.length - 1) / vid.duration);
    }
  };

  const onLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const onEnded = () => {
    setPlaying(false);
    setShowCtrls(true);
    setProgress(0);
  };

  /* ── Controls ── */
  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play();
      setPlaying(true);
    } else {
      vid.pause();
      setPlaying(false);
      setShowCtrls(true);
    }
    showControls();
  };

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  };

  const seek = (e) => {
    const vid = videoRef.current;
    if (!vid) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    vid.currentTime = pct * vid.duration;
    setProgress(pct);
    showControls();
  };

  const toggleNativeFs = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.()
        .then(() => setFullscreen(true))
        .catch(() => {});
    } else {
      document
        .exitFullscreen?.()
        .then(() => setFullscreen(false))
        .catch(() => {});
    }
  };

  return (
    <motion.div
      ref={containerRef}
      className="vp"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onMouseMove={showControls}
      onClick={showControls}
    >
      {/* ── Video element ── */}
      <video
        ref={videoRef}
        className="vp__video"
        src={VIDEO_SRC}
        playsInline
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onEnded={onEnded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      {/* ── Click-to-play centre overlay ── */}
      {!needsGesture && <div className="vp__click-area" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} />}

      {/* ── Gesture gate — shown when opened via URL, needs one tap to enter fullscreen ── */}
      {needsGesture && (
        <div className="vp__gesture-gate" onClick={enterFullscreenOnGesture}>
          <div className="vp__gesture-inner">
            <div className="vp__gesture-ring">
              <FaPlay className="vp__gesture-icon" />
            </div>
            <p className="vp__gesture-label">Tap to watch in full screen</p>
            <button
              type="button"
              className="vp__gesture-back"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      )}

      {/* ── Controls overlay ── */}
      <AnimatePresence>
        {showCtrls && (
          <motion.div className="vp__controls" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            {/* Top bar — back button */}
            <div className="vp__top-bar">
              <button type="button" className="vp__back-btn" onClick={handleClose} aria-label="Close video">
                <HiArrowLeft className="vp__back-icon" />
                <span>Back</span>
              </button>

              {/* Film title */}
              <div className="vp__title">
                <span className="vp__title-eyebrow">FORGE RETREAT</span>
                <span className="vp__title-name">The Film</span>
              </div>
            </div>

            {/* Centre big play/pause (only when paused) */}
            {!playing && (
              <motion.button
                type="button"
                className="vp__centre-play"
                onClick={togglePlay}
                aria-label="Play"
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.85, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaPlay />
              </motion.button>
            )}

            {/* Bottom bar — scrubber + mini controls */}
            <div className="vp__bottom-bar">
              {/* Scrubber */}
              <div className="vp__scrubber" onClick={seek} role="slider" aria-label="Video progress" aria-valuenow={Math.round(progress * 100)}>
                <div className="vp__scrubber-track">
                  {/* Buffered */}
                  <div className="vp__scrubber-buffered" style={{ width: `${buffered * 100}%` }} />
                  {/* Played */}
                  <div className="vp__scrubber-played" style={{ width: `${progress * 100}%` }} />
                  {/* Thumb */}
                  <div className="vp__scrubber-thumb" style={{ left: `${progress * 100}%` }} />
                </div>
              </div>

              {/* Controls row */}
              <div className="vp__controls-row">
                <div className="vp__controls-left">
                  {/* Play/Pause */}
                  <button type="button" className="vp__ctrl-btn" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"}>
                    {playing ? <FaPause /> : <FaPlay />}
                  </button>

                  {/* Mute */}
                  <button type="button" className="vp__ctrl-btn" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                    {muted ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>

                  {/* Time */}
                  <span className="vp__time">
                    {fmt(current)} / {fmt(duration)}
                  </span>
                </div>

                <div className="vp__controls-right">
                  {/* Fullscreen toggle */}
                  <button type="button" className="vp__ctrl-btn" onClick={toggleNativeFs} aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
                    {fullscreen ? <FaCompress /> : <FaExpand />}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
